"use client";

/**
 * TrxCardBrandMark — the CANONICAL host-DOM card-network mark for every TRX
 * PayPage-Fields surface.
 *
 * We render the brand ourselves rather than letting the library paint it,
 * because the library's version is a background image tiled across the
 * card-number input — see the suppression block in
 * {@link ../trx/trxFieldStyles.TRX_FIELD_CSS_NUMBER}, which every TRX card
 * surface ships. Suppression and this component are two halves of one
 * pattern: never adopt one without the other, or the field loses all brand
 * feedback.
 *
 * This is possible at all because the library reports the detected brand OUT
 * to our DOM through `onCardTypeChange`, so the cardholder gets live brand
 * recognition even though the digits never leave the cross-origin frame.
 *
 * Marks are a uniform 38×24 so swapping brands never reflows the row.
 */

import type { ReactNode } from "react";

/** Networks the TRX library reports, plus the unrecognized-so-far placeholder. */
export type CardBrand =
  | "visa"
  | "mastercard"
  | "amex"
  | "discover"
  | "jcb"
  | "generic";

/**
 * Map the library's `onCardTypeChange` label onto a {@link CardBrand}.
 * Anything unrecognized (including the empty string it emits before enough
 * digits are typed) becomes `"generic"`, so callers can render unconditionally.
 */
export function normalizeCardBrand(raw: string | undefined): CardBrand {
  switch ((raw ?? "").toLowerCase()) {
    case "visa":
      return "visa";
    case "mastercard":
    case "master card":
      return "mastercard";
    case "amex":
    case "american express":
      return "amex";
    case "discover":
      return "discover";
    case "jcb":
      return "jcb";
    default:
      return "generic";
  }
}

export interface TrxCardBrandMarkProps {
  readonly brand: CardBrand;
  /**
   * Class applied to the `<svg>`. Defaults to the `op-pay-chip` pop-in used by
   * the checkout and management slabs; surfaces on another CSS namespace pass
   * their own. Callers that animate the swap should also key the element on
   * the brand so React remounts it.
   */
  readonly className?: string;
}

export function TrxCardBrandMark({
  brand,
  className = "op-pay-chip",
}: TrxCardBrandMarkProps) {
  const frame = (children: ReactNode, label: string) => (
    <svg
      className={className}
      width="38"
      height="24"
      viewBox="0 0 38 24"
      role="img"
      aria-label={label}
      fill="none"
    >
      <rect
        x="0.5"
        y="0.5"
        width="37"
        height="23"
        rx="4.5"
        fill="#FFFFFF"
        stroke="#E7E8EC"
      />
      {children}
    </svg>
  );

  switch (brand) {
    case "visa":
      return frame(
        <text
          x="19"
          y="16"
          textAnchor="middle"
          fontFamily="Georgia, 'Times New Roman', serif"
          fontSize="11"
          fontStyle="italic"
          fontWeight="700"
          letterSpacing="0.5"
          fill="#1434CB"
        >
          VISA
        </text>,
        "Visa",
      );
    case "mastercard":
      return frame(
        <>
          <circle cx="16" cy="12" r="6.5" fill="#EB001B" />
          <circle cx="23" cy="12" r="6.5" fill="#F79E1B" fillOpacity="0.92" />
        </>,
        "Mastercard",
      );
    case "amex":
      return frame(
        <>
          <rect x="3" y="3" width="32" height="18" rx="2.5" fill="#1F72CF" />
          <text
            x="19"
            y="15.5"
            textAnchor="middle"
            fontFamily="-apple-system, Helvetica, Arial, sans-serif"
            fontSize="8"
            fontWeight="800"
            letterSpacing="0.3"
            fill="#FFFFFF"
          >
            AMEX
          </text>
        </>,
        "American Express",
      );
    case "discover":
      return frame(
        <>
          <text
            x="13"
            y="15.5"
            textAnchor="middle"
            fontFamily="-apple-system, Helvetica, Arial, sans-serif"
            fontSize="7"
            fontWeight="800"
            fill="#2A2A2A"
          >
            DISC
          </text>
          <circle cx="29" cy="12" r="5" fill="#F47216" />
        </>,
        "Discover",
      );
    case "jcb":
      return frame(
        <text
          x="19"
          y="16"
          textAnchor="middle"
          fontFamily="-apple-system, Helvetica, Arial, sans-serif"
          fontSize="9"
          fontWeight="800"
          letterSpacing="0.5"
          fill="#0B4EA2"
        >
          JCB
        </text>,
        "JCB",
      );
    default:
      return frame(
        <>
          <rect x="6" y="9" width="26" height="2.5" rx="1.25" fill="#C7CAD2" />
          <rect x="6" y="14" width="11" height="2.5" rx="1.25" fill="#DDE0E6" />
        </>,
        "Card",
      );
  }
}
